import React, { useEffect, useRef, useState } from "react";

interface SpeedometerChartProps {
  value: number;
  size?: number;
  experience: string;
  maxValue?: number;
  title?: string;
  animated?: boolean;
  showMarkers?: boolean;
  colorScheme?: "gradient" | "category";
}

const SpeedometerChart: React.FC<SpeedometerChartProps> = ({
  value,
  size = 150,
  experience,
  maxValue = 100,
  title = "Experience",
  animated = true,
  showMarkers = true,
  colorScheme = "gradient",
}) => {
  const [animatedValue, setAnimatedValue] = useState<number>(0);
  const requestRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const prevValueRef = useRef<number>(0);

  // Calculate color based on value
  const getValueColor = (currentValue: number): string => {
    const valuePercentage = (currentValue / maxValue) * 100;

    if (valuePercentage < 20) return "#F87070"; // Red - Junior
    if (valuePercentage < 40) return "#F8DC1E"; // Yellow - Mid-level
    if (valuePercentage < 60) return "#C2FC23"; // Yellow-green - Experienced
    if (valuePercentage < 80) return "#8AF229"; // Light green - Senior
    return "#1CED38"; // Green - Expert
  };

  // Different color schemes
  const segmentColors =
    colorScheme === "gradient"
      ? [
          "#F87070", // Red
          "#F8B65A", // Orange
          "#F8DC1E", // Yellow
          "#C2FC23", // Yellow-green
          "#8AF229", // Light green
          "#1CED38", // Green
        ]
      : [
          "#EF4444", // Red - Junior
          "#F59E0B", // Amber - Associate
          "#FBBF24", // Yellow - Mid-level
          "#84CC16", // Lime - Senior
          "#22C55E", // Green - Expert
          "#15803D", // Dark green - Principal
        ];

  const strokeWidth = Math.max(10, size * 0.13); // Responsive stroke width
  const outerRadius = (size - strokeWidth) / 2;
  const innerRadius = outerRadius - strokeWidth / 2;
  const circumference = 2 * Math.PI * outerRadius;
  const gradientCoverage = 0.8; // 80% of the circle (leaves bottom 20% empty)

  const dashArray = circumference * gradientCoverage;
  const dashOffset = (circumference * (1 - gradientCoverage)) / 2;

  const segmentCount = segmentColors.length;
  const segmentLength = dashArray / segmentCount;

  // Marker values (for tick marks)
  const markers = [0, 20, 40, 60, 80, 100].map((percent) => ({
    percent,
    value: (percent / 100) * maxValue,
  }));

  // Smooth animation using requestAnimationFrame
  const animateValue = (timestamp: number) => {
    if (!startTimeRef.current) {
      startTimeRef.current = timestamp;
    }

    const progress = timestamp - startTimeRef.current;
    const duration = 1500; // Animation duration in ms
    const easedProgress = easeOutCubic(Math.min(progress / duration, 1));

    const nextValue =
      prevValueRef.current + (value - prevValueRef.current) * easedProgress;

    setAnimatedValue(Math.min(nextValue, value));

    if (progress < duration && nextValue < value) {
      requestRef.current = requestAnimationFrame(animateValue);
    } else {
      setAnimatedValue(value);
    }
  };

  // Easing function for smoother animation
  const easeOutCubic = (x: number): number => {
    return 1 - Math.pow(1 - x, 3);
  };

  useEffect(() => {
    if (animated) {
      prevValueRef.current = animatedValue;
      startTimeRef.current = null;
      requestRef.current = requestAnimationFrame(animateValue);
    } else {
      setAnimatedValue(value);
    }

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [value, animated]);

  // Calculate needle angle based on the animated value
  const needleAngle =
    ((animatedValue / maxValue) * 360 * gradientCoverage - 235) % 360;
  const adjustedAngle = needleAngle < 0 ? needleAngle + 360 : needleAngle;

  // Calculate needle coordinates
  const needleInnerX =
    size / 2 + (innerRadius - 10) * Math.cos((adjustedAngle * Math.PI) / 180);
  const needleInnerY =
    size / 2 + (innerRadius - 10) * Math.sin((adjustedAngle * Math.PI) / 180);
  const needleOuterX =
    size / 2 + (outerRadius + 5) * Math.cos((adjustedAngle * Math.PI) / 180);
  const needleOuterY =
    size / 2 + (outerRadius + 5) * Math.sin((adjustedAngle * Math.PI) / 180);

  // Calculate needle base coordinates
  const needleWidth = Math.max(4, size * 0.04); // Responsive needle width
  const needleBaseLeftX =
    needleInnerX -
    (needleWidth / 2) * Math.sin((adjustedAngle * Math.PI) / 180);
  const needleBaseLeftY =
    needleInnerY +
    (needleWidth / 2) * Math.cos((adjustedAngle * Math.PI) / 180);
  const needleBaseRightX =
    needleInnerX +
    (needleWidth / 2) * Math.sin((adjustedAngle * Math.PI) / 180);
  const needleBaseRightY =
    needleInnerY -
    (needleWidth / 2) * Math.cos((adjustedAngle * Math.PI) / 180);

  // Create a shadow filter for the needle
  const filterId = `needle-shadow-${Math.random().toString(36).substring(2, 9)}`;

  // Get descriptive experience level
  const getExperienceLevel = (years: number): string => {
    if (years < 2) return "Junior";
    if (years < 4) return "Mid-level";
    if (years < 7) return "Senior";
    if (years < 10) return "Expert";
    return "Principal";
  };

  return (
    <div className="relative flex items-center justify-center w-full h-full">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="transform -rotate-[55deg]" // Rotate to position the empty space at bottom
        aria-label={`Experience meter showing ${experience} years, which is ${getExperienceLevel(parseInt(experience))} level`}
      >
        {/* Shadow filter definition */}
        <defs>
          <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="0" stdDeviation="2" floodOpacity="0.3" />
          </filter>

          {/* Gradient definitions for each segment */}
          {segmentColors.map((color, index) => (
            <linearGradient
              key={`gradient-${index}`}
              id={`segment-gradient-${index}`}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor={color} stopOpacity="0.8" />
              <stop offset="100%" stopColor={color} stopOpacity="1" />
            </linearGradient>
          ))}
        </defs>

        {/* Background track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={outerRadius}
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${dashArray} ${circumference - dashArray}`}
          strokeDashoffset={-dashOffset}
          strokeLinecap="round"
          opacity="0.3"
        />

        {/* Colored segments */}
        {segmentColors.map((color, index) => (
          <circle
            key={`segment-${index}`}
            cx={size / 2}
            cy={size / 2}
            r={outerRadius}
            stroke={`url(#segment-gradient-${index})`}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${segmentLength} ${circumference - segmentLength}`}
            strokeDashoffset={-dashOffset - segmentLength * index}
            strokeLinecap="round"
          />
        ))}

        {/* Inner circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={innerRadius - 5}
          stroke="#e5e7eb"
          strokeWidth={1}
          fill="#f9fafb"
          strokeLinecap="round"
        />

        {/* Tick marks for the scale */}
        {showMarkers &&
          markers.map((marker, index) => {
            const markerAngle =
              ((marker.percent / 100) * 360 * gradientCoverage - 235) % 360;
            const adjMarkerAngle =
              markerAngle < 0 ? markerAngle + 360 : markerAngle;

            const markerX1 =
              size / 2 +
              (innerRadius - 5) * Math.cos((adjMarkerAngle * Math.PI) / 180);
            const markerY1 =
              size / 2 +
              (innerRadius - 5) * Math.sin((adjMarkerAngle * Math.PI) / 180);
            const markerX2 =
              size / 2 +
              (innerRadius + 2) * Math.cos((adjMarkerAngle * Math.PI) / 180);
            const markerY2 =
              size / 2 +
              (innerRadius + 2) * Math.sin((adjMarkerAngle * Math.PI) / 180);

            return (
              <line
                key={`marker-${index}`}
                x1={markerX1}
                y1={markerY1}
                x2={markerX2}
                y2={markerY2}
                stroke="#9ca3af"
                strokeWidth={index % 2 === 0 ? 2 : 1}
                strokeLinecap="round"
              />
            );
          })}

        {/* Needle with shadow effect */}
        <g filter={`url(#${filterId})`}>
          <polygon
            points={`${needleBaseLeftX},${needleBaseLeftY} ${needleBaseRightX},${needleBaseRightY} ${needleOuterX},${needleOuterY}`}
            fill={getValueColor(animatedValue)}
            stroke="#ffffff"
            strokeWidth="0.5"
          />

          {/* Needle pivot */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={needleWidth * 0.8}
            fill={getValueColor(animatedValue)}
            stroke="#ffffff"
            strokeWidth="1"
          />
        </g>
      </svg>

      {/* Center text with experience information */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <p className="text-center text-gray-500 text-xs font-medium">{title}</p>
        <p className="text-center text-2xl font-bold">{experience}</p>
        <p className="text-center text-sm text-gray-700">years</p>
        <p className="text-center text-xs font-medium mt-1 text-gray-500">
          {getExperienceLevel(parseInt(experience))}
        </p>
      </div>
    </div>
  );
};

export default SpeedometerChart;
